/** Climb entry twins and an ordered face plan. Walk pricing, truncation and persistence belong to the caller. */
import { HandBack, Refusal, ecChanges, ecId, ecLean, itemField, record, upper, type JanusCtx, type JanusReaders, type Row } from '../ability/context.js';
import { PAYLOAD_DRY_PATTERNS, payloadDryRunRollSource } from '../ability/environmental-payload.js';
import { recordingRollSource, type RollSource } from '../actions/roll-source.js';
import { selectSkillForContestingRoll } from '../ability/select-skill-for-contesting-roll.js';
import { skillRollArr } from '../ability/skill-roll.js';
import { classifyCreatureNaturalTraversal } from '../compile/creature-natural-traversal.js';
import { readCreatureStatuses } from '../compile/creature-status-reader.js';
import { readItemField } from '../compile/item-field-reader.js';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers.js';
import { phpJsonInt, phpJsonNumber, phpJsonString } from '../compile/php-json-values.js';
import { submersionAt, type ElevationLink, type WaterModel } from './traversal-3d.js';
import type { CreatureNaturalTraversalInput } from '../types.js';
import { decisionWrites, movementDecisionView, finishMovementDecision } from './decision-view.js';

export const CLIMB_FORTITUDE = '24C07918-39D5-4317-A958-BDF68B633A21';
export const CLIMB_DC_ROLL = 'ED5B44EA-7438-9582-A388-04EFF342E460';
export const CLIMB_SPIDER_EFFECT = 'B9DC5478-8F86-29F7-15D8-1C8CE8171F12';
export type ClimbTier = 'spider' | 'native' | 'derived' | 'blocked';
export type ClimbReaders = Pick<JanusReaders, 'value' | 'entries' | 'definition'>;
export interface ClimbFoot { x: number; y: number; z: number }

/** ft3d_climb_tier: caster statuses precede current speed, which precedes raw pre-mount provenance. */
export function climbTier(actor: Row, read: ClimbReaders): ClimbTier {
  const dependencies = { resolveCarrierRef: read.definition };
  for (const status of Object.values(readCreatureStatuses(actor, 'caster', read.definition))) {
    const row = record(status);
    if (row && phpJsonString(readItemField(row, 'item_id', dependencies)) === CLIMB_SPIDER_EFFECT) return 'spider';
  }
  if (read.entries(actor, 'status_effect_permanent').some(entry => readLayerListEntryUuid(entry) === CLIMB_SPIDER_EFFECT)) return 'spider';
  if (phpJsonNumber(read.value(actor, 'speed_climbing') ?? 0) <= 0) return 'blocked';
  const slot = record(record(record(actor.computed)?.creature)?.speeds)?.climbing;
  return classifyCreatureNaturalTraversal({ swimming: null, climbing: slot as CreatureNaturalTraversalInput['climbing'] }).naturalClimbing
    ? 'native' : 'derived';
}

/** ft3d_climb_denial_reason: compiled hand counts, with positive tests rather than a count sum. */
export function climbDenialReason(actor: Row, read: ClimbReaders): string | null {
  const tier = climbTier(actor, read);
  if (tier === 'spider') return null;
  if (tier === 'blocked') return 'You cannot climb — your climbing speed is 0.';
  const main = phpJsonInt(read.value(actor, 'count_main_hand_equipped') ?? 0);
  const off = phpJsonInt(read.value(actor, 'count_off_hand_equipped') ?? 0);
  const both = phpJsonInt(read.value(actor, 'count_both_hands_equipped') ?? 0);
  if (tier === 'native') return both > 0 || (main > 0 && off > 0)
    ? 'You need at least one free hand to use your native climbing speed.' : null;
  return main > 0 || off > 0 || both > 0 ? 'You must have both hands free to climb! Unequip your items first.' : null;
}

/** The walker's wet-face DC expression: the plane tolerance also counts zero submersion. */
export function climbEntryDc(water: WaterModel | null, current: ClimbFoot, next: ClimbFoot): 15 | 20 {
  const minZ = Math.min(current.z, next.z);
  return water && (water.level !== null && water.level >= minZ - 0.05
    || submersionAt(water, current.x, current.y, minZ) > 0
    || submersionAt(water, next.x, next.y, minZ) > 0) ? 20 : 15;
}

/** ft3d_move_climb_link_key: original ordered link index, including descending authored faces. */
export function climbLinkKey(isFlight: boolean, transitionKind: string,
  links: ReadonlyArray<Partial<ElevationLink> | null> | null, current: ClimbFoot, next: ClimbFoot): string | null {
  if (isFlight || !(Math.abs(next.z - current.z) > 1e-6)) return null;
  if (Array.isArray(links)) {
    for (const [index, link] of links.entries()) {
      if (!link || link.kind !== 'climb') continue;
      const x = link.x ?? Infinity, y = link.y ?? Infinity;
      const touches = (foot: ClimbFoot): boolean => Math.abs(x - foot.x) <= 0.05 && Math.abs(y - foot.y) <= 0.05;
      const minZ = Math.min(link.fromZ ?? 0, link.toZ ?? 0), maxZ = Math.max(link.fromZ ?? 0, link.toZ ?? 0);
      if ((touches(current) || touches(next)) && Math.min(current.z, next.z) >= minZ - 0.05 && Math.max(current.z, next.z) <= maxZ + 0.05)
        return String(index);
    }
    return null;
  }
  return transitionKind === 'climb' && next.z > current.z ? 'legacy-single-link' : null;
}

/** First foot of each face, in walk-loop order; repeated feet on a face never roll again. */
export function climbFacePlan(samples: readonly ClimbFoot[], links: ReadonlyArray<Partial<ElevationLink> | null> | null,
  transitionKind: string, water: WaterModel | null): Array<{ key: string; step: number; dc: 15 | 20 }> {
  const seen = new Set<string>(), faces: Array<{ key: string; step: number; dc: 15 | 20 }> = [];
  for (let step = 1; step < samples.length; step++) {
    const current = samples[step - 1]!, next = samples[step]!;
    const key = climbLinkKey(false, transitionKind, links, current, next);
    if (key === null || seen.has(key)) continue;
    seen.add(key);
    faces.push({ key, step, dc: climbEntryDc(water, current, next) });
  }
  return faces;
}

/** Direct ft3d_climb_entry_check twin. Deliberately does NOT enforce hands/blocked denial.
 * Mutations and roll rows stay in the supplied engine ctx; native/spider/blocked draw nothing.
 */
export function climbEntryCheck(ctx: JanusCtx, actor: Row, dc: number): boolean {
  if (climbTier(actor, ctx.read) !== 'derived') return true;
  const skill = selectSkillForContestingRoll(ctx, actor, [CLIMB_FORTITUDE]);
  const read = ctx.read, privateBefore = ctx.rollPrivate, rowsBefore = ctx.rows.length;
  // PHP supplies item_id_used_arr: []. The generic engine requires a held used-item row:
  // resolve ONLY the empty id to that explicit empty row, never to the Fortitude definition.
  ctx.read = { ...read, definition: id => id === '' ? {} : read.definition(id) };
  ctx.rollPrivate = 0;
  try {
    const result = skillRollArr(ctx, { casterId: ecId(actor), targetIds: [], abilityId: '', rawDiceMod: 0, rawDiceAdvDisStacks: 0,
      useAbilityVariable1: null, useAbilityVariable2: null }, actor, skill, null, Math.max(1, dc),
      { rollType: CLIMB_DC_ROLL, usedRollType: '', rollItemId: '', isTarget: false, asTarget: false });
    return Math.trunc(result) >= Math.max(1, dc);
  } finally {
    // LogRow otherwise inherits privacy when the envelope is serialized. Preserve the
    // helper's public default even when a later caller restores a secret roll context.
    for (const row of ctx.rows.slice(rowsBefore)) row.roll_private ??= 0;
    ctx.read = read;
    ctx.rollPrivate = privateBefore;
  }
}

/** Single-entry caller composition: denial short-circuits BEFORE the roll source is touched.
 * Caller supplies the DC (normally climbEntryDc), ctx/roll source and held compile/catalog facts.
 * It owns per-face memoization and must halt before paying/entering a failed step.
 */
export function planClimbEntry(ctx: JanusCtx, actor: Row, dc: number): { tier: ClimbTier; denial: string | null; dc: number; success: boolean } {
  const tier = climbTier(actor, ctx.read), denial = climbDenialReason(actor, ctx.read);
  return { tier, denial, dc: Math.max(1, dc), success: denial === null && climbEntryCheck(ctx, actor, dc) };
}

export interface ClimbFace { key: string; step: number; dc: number }
export interface ClimbChecksInput { encounter: Row; members: ReadonlyMap<string, Row>; actorId: string; faces: ClimbFace[]; read: JanusReaders }
export interface ClimbEvidence { tier: 'derived'; faces: ClimbFace[]; rolled: number; failed: number | null; verdict: {
  rows: Array<JanusCtx['rows'][number] & { private: 0 }>; ec: Record<string, Record<string, unknown>>; ecBefore: Record<string, Record<string, unknown>>;
  pc: NonNullable<JanusCtx['playerCharactersChanged']>; statusesAdded: JanusCtx['statusesAdded'];
} }
export type ClimbChecksPlan = { kind: 'handback'; reason: string } | { kind: 'resolved'; failed: number | null; evidence: ClimbEvidence };
const CLIMB_ROW_TYPES = new Set([CLIMB_DC_ROLL, 'unlucky_roll', 'gained_ascension_skill_point',
  'failed_roll_for_ascension_skill_point', 'ascension_skill_tier_up', 'no_roll', 'reaction_ability_applied', 'status_effect_applied']);
/** A natural 20 on the face check grants the roller Fortunate (skill_roll_arr), exactly as on an Overrun contest. */
export const CLIMB_FORTUNATE = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
function climbContext(input: ClimbChecksInput, roll: RollSource): JanusCtx {
  return { ...movementDecisionView(input.encounter, input.members),
    read: input.read, roll, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
    statusConnectedId: null, rollPrivate: 0 };
}
/** The climb receiver can record only mover rolls, luck and one ascension column. */
function climbCommitShape(ctx: JanusCtx, input: ClimbChecksInput): string | null {
  const mover = upper(input.actorId);
  if (ctx.fortunateOffers?.size) return 'fortunate-offer-not-modelled';
  // MV11 S3 (staging): the max-dice preflight's natural 20 grants Fortunate — the one status the climb recorder saves (the mover's own).
  if (ctx.statusesAdded.length > 1 || ctx.statusesAdded.some(s => upper(s.target) !== mover || upper(s.status_item_id) !== CLIMB_FORTUNATE))
    return 'unsupported-writes';
  if (ctx.statusesRemoved.length || ctx.chargesChanged.length || ctx.inventoryChanged.length
    || ctx.statusStacks?.length || ctx.concentrationBreaks?.length || ctx.deathTail?.length) return 'unsupported-writes';
  if (ctx.rows.length > 64) return 'row-limit';
  if (ctx.rows.some(r => !CLIMB_ROW_TYPES.has(r.roll_type_1))) return 'row-type';
  if (ctx.rows.some(r => upper(r.roll_by) !== mover)) return 'row-by';
  for (const [id, cols] of Object.entries(ecChanges(ctx)))
    // luck (natural 1 / 20) and the reaction an auto-fired Fortunate spends (applyUniversalFortunateOnLuckGain) — the mover's own row only
    if (upper(id) !== mover || Object.keys(cols).some(col => col !== 'luck' && col !== 'reaction')) return 'column-outside-allowlist';
  const actor = ctx.members.get(mover)!;
  const itemId = upper(ecLean(actor, 'item_id') ?? itemField(actor, 'item_id'));
  const pc = ctx.playerCharactersChanged ?? [];
  if (pc.some(p => !itemId || upper(p.item_id) !== itemId || upper(p.skill_id) !== CLIMB_FORTITUDE
    || !/^sp_as_[a-z0-9_]+$/.test(p.column))) return 'pc-outside-allowlist';
  // Consecutive faces can gain the same ascension column more than once. Record its first before and final after once.
  if (pc.length > 1) {
    const first = pc[0]!;
    let after = first.after;
    for (const next of pc.slice(1)) {
      if (next.column !== first.column || next.before !== after) return 'pc-outside-allowlist';
      after = next.after;
    }
    ctx.playerCharactersChanged = [{ ...first, after }];
  }
  return null;
}
const climbError = (error: unknown): string => error instanceof HandBack ? error.reason
  : error instanceof Refusal ? error.refusal : String(error).slice(0, 120);
/** MV12 (coordinator Round 37a): per-face outcome classes. Whole-walk patterns alternate individual DICE, so a face that spends
 *  two dice (d20 + tier die) always lands the same natural in alt-hi / alt-lo, and a nat 1 on one face followed by a nat 20 on
 *  the next (luck down, then back up — a luck GAIN the first face made possible) was never preflighted. Each face is now drawn
 *  under every class from every distinct state the earlier faces can leave. */
// Codex r1a P2: max / alt-hi both pair a natural 20 with a PASSED ascension die; alt2-* / alt3-* reach natural 20 + FAILED ascension
// (d20, tier die, ascension d20) and the advantage pairs. Every dry pattern, per face.
const FACE_PATTERNS = PAYLOAD_DRY_PATTERNS;
const CLIMB_BRANCH_LIMIT = 64;
/** Branch on the earlier face's writes; untouched creature/scene inputs remain shared. */
function forkClimbCtx(ctx: JanusCtx): JanusCtx {
  finishMovementDecision(ctx);
  const out: Record<string, unknown> = { ...ctx };
  for (const [key, value] of Object.entries(ctx)) {
    if (['read', 'roll', 'originals', 'members', 'encounter'].includes(key) || typeof value === 'function') continue;
    if (value instanceof Set) out[key] = new Set(value);
    else if (value instanceof Map) out[key] = new Map(value);
    else if (value !== null && typeof value === 'object') out[key] = structuredClone(decisionWrites(value)); // dnr-clone: allow size=one face's changed write queue; why=extreme-dice branches retain independent effect writes
  }
  Object.assign(out, movementDecisionView(ctx.encounter, ctx.members), { originals: ctx.originals });
  return out as unknown as JanusCtx;
}
/** The state a later face can observe: every member column change, the statuses / PC column written, open offers. Row count is
 *  not part of it (only the row limit reads it) — a merge keeps the longer log. */
const climbStateKey = (ctx: JanusCtx): string => JSON.stringify([ecChanges(ctx),
  ctx.statusesAdded.map(s => [upper(s.target), upper(s.status_item_id)]), ctx.playerCharactersChanged ?? [], ctx.fortunateOffers?.size ?? 0]);
function preflightFaceOutcomes(input: ClimbChecksInput): string | null {
  const mover = upper(input.actorId);
  const root = climbContext(input, payloadDryRunRollSource('max'));
  root.dryRun = true;
  let frontier: Array<{ ctx: JanusCtx; trail: string[] }> = [{ ctx: root, trail: [] }];
  for (const face of input.faces) {
    const next = new Map<string, { ctx: JanusCtx; trail: string[] }>();
    for (const state of frontier) for (const pattern of FACE_PATTERNS) {
      const trail = [...state.trail, pattern];
      try {
        const dry = forkClimbCtx(state.ctx);
        dry.roll = payloadDryRunRollSource(pattern);
        // A failed face still continues the preflight (as the whole-walk passes do): live dice stop there, so this only over-covers.
        climbEntryCheck(dry, dry.members.get(mover)!, face.dc);
        const why = climbCommitShape(dry, input);
        if (why) throw new HandBack(why);
        const key = climbStateKey(dry), held = next.get(key);
        if (!held || held.ctx.rows.length < dry.rows.length) next.set(key, { ctx: dry, trail });
      } catch (error) { return `climb:preflight:faces:${trail.join('>')}:${climbError(error)}`; }
    }
    if (next.size > CLIMB_BRANCH_LIMIT) return 'climb:preflight:branch-limit';
    frontier = [...next.values()];
  }
  return null;
}
/** Every face is preflighted in order, including faces beyond a synthetic failure. Live dice stop at the first failure. */
export function resolveClimbChecks(input: ClimbChecksInput, roll: RollSource): ClimbChecksPlan {
  if (!input.read.definition(CLIMB_FORTITUDE)) return { kind: 'handback', reason: 'climb:definition-not-held' };
  if (![...input.members.keys()].some(id => upper(id) === upper(input.actorId))) return { kind: 'handback', reason: 'climb:mover-not-held' };
  if (input.faces.length < 1 || input.faces.length > 32 || input.faces.some(face => typeof face.key !== 'string' || face.key.length > 32
    || !Number.isSafeInteger(face.step) || face.step < 1 || !Number.isSafeInteger(face.dc) || face.dc < 1 || face.dc > 100))
    return { kind: 'handback', reason: 'climb:face-shape' };
  for (const pattern of ['max', 'min', 'alt-hi', 'alt-lo'] as const) {
    try {
      const dry = climbContext(input, payloadDryRunRollSource(pattern));
      dry.dryRun = true;
      for (const face of input.faces) {
        climbEntryCheck(dry, dry.members.get(upper(input.actorId))!, face.dc);
        const why = climbCommitShape(dry, input);
        if (why) throw new HandBack(why);
      }
    } catch (error) { return { kind: 'handback', reason: `climb:preflight:${pattern}:${climbError(error)}` }; }
  }
  const mixed = preflightFaceOutcomes(input);
  if (mixed) return { kind: 'handback', reason: mixed };
  try {
    const ctx = climbContext(input, recordingRollSource(roll));
    let failed: number | null = null, rolled = 0;
    for (const [index, face] of input.faces.entries()) {
      const passed = climbEntryCheck(ctx, ctx.members.get(upper(input.actorId))!, face.dc);
      rolled++;
      const why = climbCommitShape(ctx, input);
      if (why) throw new HandBack(why);
      if (!passed) { failed = index; break; }
    }
    const ec: ClimbEvidence['verdict']['ec'] = {}, ecBefore: ClimbEvidence['verdict']['ecBefore'] = {};
    for (const [id, cols] of Object.entries(ecChanges(ctx))) for (const [col, value] of Object.entries(cols)) {
      const before = ecLean(ctx.originals.get(upper(id))!, col);
      if (String(before ?? '') === String(value ?? '')) continue;
      (ec[upper(id)] ??= {})[col] = value; (ecBefore[upper(id)] ??= {})[col] = before == null ? null : Number(before);
    }
    finishMovementDecision(ctx);
    return decisionWrites({ kind: 'resolved', failed, evidence: { tier: 'derived', faces: input.faces.map(({ key, step, dc }) => ({ key, step, dc })),
      rolled, failed, verdict: { rows: ctx.rows.map(row => ({ ...row, private: 0 as const })), ec, ecBefore, pc: ctx.playerCharactersChanged ?? [], statusesAdded: ctx.statusesAdded } } });
  } catch (error) { return { kind: 'handback', reason: `climb:real-draw-exception:${climbError(error)}` }; }
}
